import { format, isValid, parse } from "date-fns";
import { CalendarSearch } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type { FormControlProps } from "./types";

export const DateFormControl = (props: FormControlProps) => {
  const { field, name } = props;
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { control, getValues } = useFormContext();
  const currentValue: string = getValues(name);
  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    if (currentValue) {
      const savedDate = new Date(currentValue);
      if (isValid(savedDate)) {
        setDisplayValue(format(savedDate, "P"));
      }
    } else {
      setDisplayValue("");
    }
    // Seeds from persisted form state on mount. Re-running would overwrite
    // a partially typed date.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormField
      control={control}
      name={name}
      render={(context) => {
        // value is the date in string ISO format
        const { onChange, value } = context.field;

        const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
          const inputValue = evt.target.value;
          setDisplayValue(inputValue);
          const parsedDate = parse(inputValue, "P", new Date());
          onChange(isValid(parsedDate) ? parsedDate.toISOString() : inputValue);
        };
        const selectedDate = value ? new Date(value as string) : undefined;
        return (
          <FormItem>
            <FormLabel>{field.title}</FormLabel>
            <FormControl>
              <div
                className={cn(
                  "flex gap-x-2 w-full rounded-md border border-input bg-background text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground has-focus-visible:ring-ring items-center p-0 has-focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                )}
              >
                <input
                  className="h-10 px-3 py-2 flex-1 bg-transparent placeholder:text-muted-foreground outline-hidden"
                  type="text"
                  placeholder={field.title}
                  autoComplete="off"
                  {...context.field}
                  onChange={handleChange}
                  value={displayValue}
                />

                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button className="p-2">
                      <CalendarSearch className="size-5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={
                        selectedDate && isValid(selectedDate)
                          ? selectedDate
                          : undefined
                      }
                      weekStartsOn={1}
                      onSelect={(date) => {
                        if (!date) {
                          onChange(undefined);
                        } else {
                          onChange(date.toISOString());
                          setDisplayValue(format(date, "P"));
                        }
                        setCalendarOpen(false);
                      }}
                      disabled={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </FormControl>
            {field.description && (
              <FormDescription>{field.description}</FormDescription>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
